import axios from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import { activeOrgForRequests } from "@/components/main/stores/orgContextStore";

/** Why a hit is in reach without belonging to the ceiling org (D56).
 *  Absent ⇒ the hit IS the ceiling org's own. Only an import search labels. */
export type ResultOrigin = "personal_account" | "shared";

/** Mirrors the serde shapes in `server/src/api/search.rs`. */
export interface SearchModelResult {
  id: string;
  name: string;
  description: string | null;
  /** `"Owner"` or `"Shared"` — rendered below the model name. */
  relation: string;
  relevance_score: number;
  origin?: ResultOrigin;
}

export interface SearchTableResult {
  id: string;
  name: string;
  description: string | null;
  /** `name: null` ⇔ the caller cannot view the parent model (or it is
   *  trashed) ⇔ no data view — the row renders quiet-locked. */
  parent_model: { id: string; name: string | null };
  /** Omitted on the wire when unknown — absence ≠ 0. */
  link_count?: number;
  relevance_score: number;
  /** The origin of the PARENT model — a table has no org of its own. */
  origin?: ResultOrigin;
}

export interface SearchDatasetResult {
  id: string;
  name: string;
  description: string | null;
  /** Advisory count of members the caller can currently discover. */
  member_count: number;
  /** `true` if the caller owns the dataset, else it is shared with them. */
  is_owner: boolean;
  relevance_score: number;
  origin?: ResultOrigin;
}

/** A recipe the caller may see (R7): published in an org they belong to, or
 *  their own — with every table in its closure readable either way. */
export interface SearchRecipeResult {
  id: string;
  name: string;
  description: string | null;
  /** The current version — the one a run resolves to. */
  version: number;
  /** A private hit is the caller's own. */
  state: "private" | "published";
  relevance_score: number;
  /** The origin of the recipe's own org — a recipe belongs to exactly one. */
  origin?: ResultOrigin;
}

export interface SearchResponse {
  models: SearchModelResult[];
  tables: SearchTableResult[];
  datasets: SearchDatasetResult[];
  recipes: SearchRecipeResult[];
  /** Present only for an import search — names the group every unlabelled
   *  hit belongs to. `name` is absent for an org the caller cannot enter. */
  ceiling?: { org_id: string; name?: string; is_personal: boolean };
}

/** 429 from the per-keystroke governor — transient; callers keep stale results. */
export class SearchRateLimitedError extends Error {
  constructor() {
    super("search rate limited");
    this.name = "SearchRateLimitedError";
  }
}

export interface SearchOptions {
  /** Search to import INTO this model: its org becomes the ceiling, and the
   *  rail's active org is deliberately NOT sent — on the canvas route that is
   *  whatever the overview last left behind, absent entirely on a cold load. */
  importInto?: string;
}

export const searchApp = async (
  q: string,
  signal: AbortSignal,
  options?: SearchOptions,
): Promise<SearchResponse> => {
  try {
    const params: Record<string, string> = { q };
    if (options?.importInto) {
      params.import_into = options.importInto;
    } else {
      const org = activeOrgForRequests();
      if (org.kind === "stale") {
        return { models: [], tables: [], datasets: [], recipes: [] };
      }
      if (org.kind === "org") params.org_id = org.orgId;
    }
    const response = await axiosInstance.get<SearchResponse>("/api/search", {
      params,
      signal,
    });
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 429) {
      throw new SearchRateLimitedError();
    }
    throw err;
  }
};

/** Mirrors `server/src/api/tables/columns.rs`. */
export interface TableColumnsResponse {
  columns: {
    id: string;
    name: string | null;
    /** `PhysicalKind` display label; empty for not-yet-typed columns. */
    type: string;
    /** The `PhysicalKind` variant name. */
    kind?: string;
  }[];
  /** At the version an import would pin. */
  active_rows?: number;
}

export const fetchTableColumns = async (
  tableId: string,
): Promise<TableColumnsResponse> => {
  const response = await axiosInstance.get<TableColumnsResponse>(
    `/api/tables/${encodeURIComponent(tableId)}/columns`,
  );
  return response.data;
};
